import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, Search, ChevronRight, Clock, RefreshCw, AlertCircle, Loader2, UserCheck } from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import Badge from '../../components/Badge';

export default function Conversations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getConversations(id, { search: searchQuery });
      setConversations(response.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchConversations();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchConversations]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  // Format phone number with spacing for readability (e.g., 254 723 068 001)
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length <= 4) return cleaned;
    if (cleaned.length <= 7) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  };

  // Get display name for conversation, with formatted phone
  const getDisplayName = (conv) => {
    if (conv.contactName) return conv.contactName;
    const phone = conv.phone || conv.sender_phone;
    if (phone) return formatPhoneNumber(phone);
    return 'Unknown';
  };

  // Check if conversation needs human attention (AI paused OR human help requested)
  const needsAttention = (conv) => {
    return conv.aiPaused || conv.needsHumanAttention;
  };

  // Show full-page loading spinner for initial load
  if (isLoading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Conversations</h1>
            <p className="text-text-secondary">View and manage customer chats</p>
          </div>
        </div>
        <button
          onClick={fetchConversations}
          disabled={isLoading}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchConversations}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {conversations.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No conversations yet"
          description="When customers message your WhatsApp, their conversations will appear here."
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-6 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Conversations List */}
          <div className="space-y-2 max-w-2xl">
            {conversations.map((conv, index) => (
              <button
                key={conv.id}
                onClick={() => navigate(`/agent/${id}/chats/${conv.id}`)}
                className="w-full card p-4 flex items-center gap-4 hover:border-primary/20 hover:shadow-soft transition-all text-left animate-slide-up group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Avatar with human attention indicator */}
                <div className="relative flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    needsAttention(conv)
                      ? 'bg-gradient-to-br from-amber-100 to-amber-200 ring-2 ring-amber-400'
                      : 'bg-gradient-to-br from-primary/20 to-accent/20'
                  }`}>
                    {needsAttention(conv) ? (
                      <UserCheck className="w-5 h-5 text-amber-600" />
                    ) : (
                      <span className="text-lg font-semibold text-primary">
                        {(conv.contactName || conv.phone || conv.sender_phone || '?').charAt(0)}
                      </span>
                    )}
                  </div>
                  {needsAttention(conv) && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                      <span className="text-[10px] text-white font-bold">!</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-semibold text-text-primary truncate">
                        {getDisplayName(conv)}
                      </h3>
                      {needsAttention(conv) && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full whitespace-nowrap">
                          {conv.aiPaused ? 'Manual mode' : 'Needs attention'}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-muted flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      {formatTime(conv.lastMessageTime)}
                    </span>
                  </div>
                  <p className="text-sm text-text-muted truncate">
                    {conv.lastMessage || 'No messages'}
                  </p>
                </div>

                {/* Unread indicator */}
                {conv.unread > 0 && (
                  <Badge variant="primary" size="small">
                    {conv.unread}
                  </Badge>
                )}

                <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>

          {conversations.length === 0 && searchQuery && (
            <div className="text-center py-12">
              <p className="text-text-muted">No conversations matching "{searchQuery}"</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}